import { isLocalUrl } from './is-local-url.js';
import { isRelativePath } from './is-relative-path.js';
/**
 * Redirects the request to a proxy server with a given URL. But not for:
 *
 * - Relative URLs
 * - URLs that seem to point to a local IP (except the proxy is on the same domain)
 * - URLs that don't look like a domain
 **/
export const redirectToProxy = (proxyUrl, url) => {
    try {
        if (!proxyUrl || !shouldUseProxy(proxyUrl, url)) {
            return url ?? '';
        }
        // Create new URL object from url
        const newUrl = new URL(url);
        // Add temporary domain for relative proxy URLs
        //
        // Q: Why do we need to add http://localhost to relative proxy URLs?
        // A: Because the `new URL()` would otherwise fail.
        //
        const temporaryProxyUrl = isRelativePath(proxyUrl) ? `http://localhost${proxyUrl}` : proxyUrl;
        // Rewrite the URL with the proxy
        newUrl.href = temporaryProxyUrl;
        // Add the original URL as a query parameter
        newUrl.searchParams.append('scalar_url', url);
        // Remove the temporary domain if we added it, but only from the start of the URL
        const result = isRelativePath(proxyUrl) ? newUrl.toString().replace(/^http:\/\/localhost/, '') : newUrl.toString();
        return result;
    }
    catch {
        return url ?? '';
    }
};
/**
 * Returns false for requests to localhost, relative URLs, if no proxy is defined …
 **/
export const shouldUseProxy = (proxyUrl, url) => {
    try {
        // ❌ We don't have a proxy URL or the URL
        if (!proxyUrl || !url) {
            return false;
        }
        // ❌ Request to relative URLs (won't be blocked by CORS anyway)
        if (isRelativePath(url)) {
            return false;
        }
        // ✅ Proxy URL is on the same domain (e.g. /proxy)
        // It's more likely (not guaranteed, though) that the proxy has access to local domains.
        if (isRelativePath(proxyUrl)) {
            return true;
        }
        // ✅ Proxy URL is local
        if (isLocalUrl(proxyUrl)) {
            return true;
        }
        // ❌ Requests to localhost
        // We won't reach them from a (likely remote) proxy.
        if (isLocalUrl(url)) {
            return false;
        }
        // ✅ Seems fine (e.g. remote proxy + remote URL)
        return true;
    }
    catch {
        return false;
    }
};
